import { dataService } from '../../data';

import { DataObject, ObjectOptions, RelatedObject } from "../base";
import { CreateObject } from "../item";
import { TicketProductView, TicketProductViewProxy } from '../views/ticketproduct';

/****************************/
/* SCHEMA DEFINITION        */
/****************************/

import { TicketType } from '../types';
import { TicketOfferType } from '../types';
import { ProductType } from '../types';
import { TicketOptionType } from '../types';

import { Ticket } from './ticket';
import { TicketOffer } from './ticketoffer';

const _schema = {
    "name": "TICKETPRODUCT",
    "fields": [
        { name: "status", type: "string", default: "AC" },
        { name: "ticket", type: "objid" },
        { name: "product", type: "objid" },
        { name: "offer", type: "objid" },
        { name: "amount", type: "number" },
        { name: "price", type: "number" },
        { name: "charge", type: "number" },
        { name: "fixed", type: "boolean" },
        { name: "refund", type: "number" },        
        { name: "taxrate", type: "number" },
        { name: "type", type: "string" },
        { name: "market", type: "number" },
        { name: "weight", type: "number" },
        { name: "flags", type: "string" },
        { name: "comments", type: "string" },
        { name: "sort", type: "number" },
        { name: "addlines", type: "number" },
        { name: "dellines", type: "number" },
        { name: "notified", type: "number" }
    ],
    relate: [
        { direction: ">", target: "TICKET", by: "ticket", name: "ticket", reason: "ticket", child: false, class: TicketType },
        { direction: ">", target: "PRODUCT", by: "product", name: "product", reason: "product", child: false, class: ProductType },
        { direction: ">", target: "TICKETOFFER", by: "offer", name: "offer", reason: "offer", child: true, class: TicketOfferType },
        { direction: "<", target: "TICKETOPTION", by: "product", name: "option", reason: "options", child: true, class: TicketOptionType }
    ]
} as const;

const _TicketProductClass = CreateObject(_schema);
if (!_TicketProductClass) {
    throw new Error("Failed to create BaseClass for '" + _schema.name + "'");
}

/****************************/
/* CLASS DECLARATION        */
/****************************/

enum TicketProductFlags {
    IsAC = 0,    // line was active in cart
    IsRD = 1,    // line was ready (kitchen / ticket ready)
    IsPP = 2,    // line was payment pending
    IsPD = 3,    // line was paid
    IsCC = 4,    // line was cancelled
    IsDE = 5,    // line was deleted
}

export class TicketProduct extends _TicketProductClass { 
    private static readonly _EventIndex: Record<string, TicketProductFlags> = Object.freeze({
        AC: TicketProductFlags.IsAC,
        RD: TicketProductFlags.IsRD,
        PP: TicketProductFlags.IsPP,
        PD: TicketProductFlags.IsPD,
        CC: TicketProductFlags.IsCC,
        DE: TicketProductFlags.IsDE,
    });

    constructor(objid: string | null, data: dataService, objoptions: ObjectOptions | null = null){
        super(_schema, objid, data, objoptions || {});

        if (!this._data['flags']){
            this._data['flags'] = '0'.repeat(Object.keys(TicketProductFlags).length / 2);
        }

        if (!objid){
            this.status = 'AC';
            this.addlines = 0;
            this.dellines = 0;
            this.notified = 0;
        }
    }

    protected override _OnUpdate(info: any): void {
        super._OnUpdate(info);

        if (this.addlines == null){
            this.addlines = 0;
        }

        if (this.dellines == null){
            this.dellines = 0;
        }

        if (this.notified == null){
            this.notified = 0;
        }
    }

    Copy(store: Array<DataObject> = []): TicketProduct {
        return this._Copy(store) as TicketProduct;
    }

    override get viewProxy(): TicketProductView {
        return TicketProductViewProxy(this, this.data);
    }

    /**
     * Changelog rows lack parent 1:N lists (`TICKET.products`, `TICKETOFFER.products`).
     * Register on link (idempotent). Copies rebuild lists in `_Copy` and must not join.
     */
    protected override DoChildLinked(relation: string | null, related: RelatedObject | null): void {
        if (this.CopyOf){
            return;
        }

        if (relation === 'ticket' && related instanceof Ticket){
            if (related === this.ticket){
                related.AddProduct(this);
            }
            else {
                related.DelProduct(this);
            }
            return;
        }

        if (relation === 'offer' && related instanceof TicketOffer){
            if (related === this.offer){
                related.AddProduct(this);
            }
            else {
                related.DelProduct(this);
            }
        }
    }

    /****************************/
    /* FLAGS MANAGEMENT         */
    /****************************/

    protected get flagArray(): Array<boolean> {
        const _array: boolean[] = new Array(Object.keys(TicketProductFlags).length / 2).fill(false);

        if (this.flags){
            const _flags = Array.from(this.flags);
            for (let _index = 0; _index < _flags.length; _index++){
                _array[_index] = (_flags[_index] == '1');
            }
        }

        return _array;
    }

    protected set flagArray(value: Array<boolean>) {
        const _array: string[] = [];
        for (let _index = 0; _index < value.length; _index++){
            _array[_index] = (value[_index] === true) ? '1' : '0';
        }

        const _flags = _array.join('');
        if (!this.patchValue(this._data, 'flags', _flags)){
            return;
        }

        this.ToUpdate = true;
    }

    protected _setFlagValue(flag: TicketProductFlags, value: boolean){
        const _flags = this.flagArray;
        _flags[flag] = value;
        this.flagArray = _flags;
    }

    HasEvent(event: string): boolean {
        const _index = TicketProduct._EventIndex[event];
        if (_index === undefined){
            return false;
        }

        return this.flagArray[_index] || false;
    }

    AddEvent(event: string): void {
        const _index = TicketProduct._EventIndex[event];
        if (_index === undefined){
            return;
        }

        this._setFlagValue(_index, true);
    }

    DelEvent(event: string): void {
        const _index = TicketProduct._EventIndex[event];
        if (_index === undefined){
            return;
        }

        this._setFlagValue(_index, false);
    }

    /****************************/
    /* CLASS MEMBERS            */
    /****************************/
    
    get status(): string | null {
        return this['__base__status'];
    }

    set status(value: string){
        if (this.status == 'DE'){
            return;
        }

        if (![ 'PR', 'SP' ].includes(value)){
            this.AddEvent(value);
        }

        if (this.status == value){
            return;
        }

        if (this.status == 'SP' && (value != 'DE')){
            return;
        }

        if (value == 'CC'){
            const _units = this.amount || 0;
            if (!this.IsSplitter && _units > 0){
                this.dellines = (this.dellines ?? 0) + _units;
            }

            const _ticket = this.ticket;
            const _paid = _ticket?.PaidFlag === true;
            const _toPay = _ticket?.ToPayFlag === true;

            if (_paid && !_toPay){
                const _lineCharge = this.charge || 0;
                if (_lineCharge > 0){
                    this.refund = _lineCharge;
                }
            }

            this.status = 'DE';
            return;
        }

        if ([ 'PR', 'AC' ].includes(value)){
            this.DelEvent('PP');
            this.DelEvent('PD');
        }

        this['__base__status'] = value;
        if (this.ticket){
            this.ticket.DoRefresh(this.table);
        }

        if (this.offer){
            this.offer.DoRefresh(this.table);
        }
    }   
     
    get amount(): number {
        return this['__base__amount'] || 0;
    }

    set amount(value: number){
        if (this.amount == value){
            return;
        }
        
        this['__base__amount'] = value;
        if (value === 0){
            this.status = 'DE';
        }

        if (this.ticket){
            this.ticket.DoRefresh(this.table);
        }
    }

    get sort(): number {
        return this['__base__sort'] || 0;
    }

    set sort(value: number | null){
        if (this.sort == value){
            return;
        }
        
        this['__base__sort'] = value;
        if (this.ticket){
            this.ticket.DoRefresh(this.table);
        }
    }

    get refund(): number {
        return this['__base__refund'] || 0;
    }

    set refund(value: number | null){
        if (this.refund == (value || 0)){
            return;
        }

        this['__base__refund'] = value;
        this.DoRefresh(this.table, false);
        if (this.ticket){
            this.ticket.DoRefresh(this.table);
        }
    }

    /****************************/
    /* CUSTOM METHODS           */
    /****************************/

    get IsValid(): boolean {
        if (!this.status || this.status == 'RC'){
            return false;
        }

        return (this.status != 'DE') || (this.refund > 0);
    }

    /**
     * Active cart membership for comanda calculation and PARA PREPARAR.
     * Valid non-return line with units, or a kitchen splitter (amount 0 by design).
     */
    get InCart(): boolean {
        if (!this.IsValid || this.refund !== 0){
            return false;
        }

        return this.IsSplitter || this.amount > 0;
    }

    get IsSplitter(): boolean {
        return this.status === 'SP';
    }

    Reset(): void {
        this['__base__amount'] = 0;
        
        this.addlines = 0;
        this.dellines = 0;
        this.notified = 0;
    }

    AddLines(count: number): void {
        if (this.IsSplitter){
            return;
        }

        this.addlines = (this.addlines ?? 0) + count;
        this.amount = this.amount + count;
    }

    DelLines(count: number): void {
        if (this.IsSplitter){
            return;
        }

        this.dellines = (this.dellines ?? 0) + count;
        this.amount = Math.max(0, this.amount - count);
    }

    SetLines(count: number): void {
        if (this.IsSplitter){
            return;
        }

        const _next = count;
        const _prev = this.amount || 0;

        if (_next > _prev){
            this.AddLines(_next - _prev);
        }
        else if (_next < _prev){
            this.DelLines(_prev - _next);
        }
    }
}
